import React from 'react'
import { vn } from 'vend-number'
import { PERMISSIONS } from '../../../../../constants'
import { containsGiftCard } from '../../../../../functions/lineItem'
import { isABPAddedButNotPaid } from '../../../../../functions/payments'
import {
  getOutstandingBalance,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isReturn,
  isValidCustomer,
} from '../../../../../functions/sale'
import {
  useRetailerSettingsManager,
  useUserManager,
} from '../../../../../ng-compat/hooks'
import { SaleStatus } from '../../../../../models/sale.constants'
import { useAppSelector } from '../../../../../state/hooks'
import {
  selectCurrentSaleAsSale,
  selectSaleDisplayStatus,
  selectLineItems,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import { useHasOnAccountPaymentEntry } from '../../hooks/useHasOnAccountPaymentEntry'

/**
 * Hook to determine if a sale meets layaway eligibility conditions
 * so a Payment Requests can be created for it, and then the sale layawayed
 *
 * To determmine layaway eligibility we've mirrored logic from:
 * - useCustomerPaymentOptions (builds customer payment buttons including layaway)
 * - usePaymentDisabledState (determines disabled state of payment options including layaway)
 *
 * Note that we explicity do not check for isPartialPayment (like usePaymentDisabledState do),
 * to allow partial payments to be taken on layaway-eligible sales through payment requests,
 * in which case the payment request is created with the partial amount and the sale is layaway with full outstanding amount
 *
 * @param hasPaymentAmountError - Whether the payment amount has validation errors
 * @returns boolean indicating if the sale is eligible for layaway-based payment requests
 */
export const useIsLayawayEligibleForPaymentRequest = (
  hasPaymentAmountError: boolean
) => {
  const currentSale = useAppSelector(selectCurrentSaleAsSale)
  const saleDisplayStatus = useAppSelector(selectSaleDisplayStatus)
  const lineItems = useAppSelector(selectLineItems)
  const retailerSettingsManager = useRetailerSettingsManager()
  const userManager = useUserManager()
  const hasOnAccountPayment = useHasOnAccountPaymentEntry()

  const isLayawayEligible = React.useMemo(() => {
    // Check for payment amount errors
    if (hasPaymentAmountError) {
      return false
    }

    // Check balance conditions that determine if layaway button should exist
    const balance = getOutstandingBalance(currentSale)
    const hasNegativeBalance = vn(balance).isNeg()
    const notReturnSaleAndPositiveBalance =
      !isReturn(currentSale) && !hasNegativeBalance
    const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()

    const hasValidSaleConditions =
      notReturnSaleAndPositiveBalance ||
      (hasXeroEnabled && isReturn(currentSale) && !hasNegativeBalance)

    if (!hasValidSaleConditions) {
      return false
    }

    // Check layaway-specific conditions
    if (saleDisplayStatus === SaleStatus.ACCOUNT_SALE) {
      return false
    }

    // An On Account payment has already been applied to this sale (payment-type
    // flow) — mirrors the guard in useCustomerPaymentOptions.
    if (hasOnAccountPayment) {
      return false
    }

    if (!userManager.hasPermission(PERMISSIONS.SALE_LAYBY)) {
      return false
    }

    if (containsGiftCard(lineItems)) {
      return false
    }

    // Check customer validity
    if (!isValidCustomer(currentSale.customer)) {
      return false
    }

    // Check sale status conditions (excluding partial payment check)
    if (
      isMarkedAsUnfulfilled(currentSale.status) ||
      isLegacyServiceSale(currentSale.status)
    ) {
      return false
    }

    // Check for account balance payment
    if (isABPAddedButNotPaid(currentSale)) {
      return false
    }

    return true
  }, [
    currentSale,
    saleDisplayStatus,
    lineItems,
    retailerSettingsManager,
    userManager,
    hasPaymentAmountError,
    hasOnAccountPayment,
  ])

  return isLayawayEligible
}
